import moment from "moment";
import { type CSSProperties, PureComponent } from "react";

import { type TimeLocale } from "helpers/AppProvider.types";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import {
    type ITimezoneDetailsProps,
    withTimezoneDetails,
} from "commonComponents/helpers/TimeZone/TimeZoneDetails";

export interface IDateDisplayProps {
    value: moment.Moment;

    /** Shows the date (Month, Day, Year) unless it's a range
     * @default true
     */
    showDate?: boolean;

    /** Shows the time, will appear after date
     * @default false
     */
    showTime?: boolean;

    /** Shows the day of week (Mon, Tue, Wed...) at the beginning of the date
     * @default false
     */
    showDayOfWeek?: boolean;

    /** Shows the month
     * @default true
     */
    showMonth?: boolean;

    /** Shows the year
     * @default true
     */
    showYear?: boolean;

    /** Hides the current year if the date is this year
     * @default false
     */
    hideCurrentYear?: boolean;

    "data-testid"?: string;

    /** Use ONLY when static dates cannot be used at Storybook story level.
     *  Snapshots for the component will be ignored in Chromatic, if included.
     */
    "data-chromatic"?: "ignore";

    className?: string;
}

export interface IDateDisplayFormattingProps
    extends Pick<
        IDateDisplayProps,
        "showDate" | "showTime" | "showDayOfWeek" | "showMonth" | "showYear" | "hideCurrentYear"
    > {}

class DateDisplayInternal extends PureComponent<IDateDisplayProps & ITimezoneDetailsProps> {
    static defaultProps = {
        showDate: true,
        showTime: false,
        showDayOfWeek: false,
        showMonth: true,
        showYear: true,
        hideCurrentYear: false,
    };

    render() {
        const {
            value,
            showDate,
            showDayOfWeek,
            showTime,
            showMonth,
            showYear,
            hideCurrentYear,
            builderTimeLocale,
            className,
            "data-testid": dataTestId,
            "data-chromatic": dataChromatic,
        } = this.props;

        const dateStyles: CSSProperties = {
            whiteSpace: "nowrap",
        };

        if (value === null || value === undefined) {
            throw `<DateDisplay> was passed a value that was set to "${value}"`;
        }

        return (
            <span
                style={dateStyles}
                data-testid={dataTestId}
                data-chromatic={dataChromatic}
                className={className}
            >
                {formatDate(value, builderTimeLocale, {
                    showDate,
                    showTime,
                    showDayOfWeek,
                    showMonth,
                    showYear,
                    hideCurrentYear,
                })}
            </span>
        );
    }
}

/**
 * Displays a formatted date as text, use whenever you want to show date for display only
 * @note Use DateDisplayDate when **not** using `moment` values
 * @deprecated Use DateDisplayDate instead
 */
export const DateDisplay = withTimezoneDetails(DateDisplayInternal);

/**
 * @note Use DateDisplayDateOrRecentLabel when **not** using `moment` values
 * @deprecated Use DateDisplayDateOrRecentLabel instead
 */
export function getDateDisplayOrRecentLabel(
    value?: moment.Moment | null,
    showTime: boolean = true,
) {
    if (!value) {
        return friendlyDefaultDisplayValue;
    }
    const timeDisplay = showTime ? (
        <>
            {" - "}
            <DateDisplay showDate={false} showTime value={value} />
        </>
    ) : null;
    if (moment().isSame(value, "d")) {
        return (
            <>
                Today
                {timeDisplay}
            </>
        );
    } else if (moment().subtract(1, "d").isSame(value, "d")) {
        return (
            <>
                Yesterday
                {timeDisplay}
            </>
        );
    } else if (moment().add(1, "d").isSame(value, "d")) {
        return (
            <>
                Tomorrow
                {timeDisplay}
            </>
        );
    }
    return <DateDisplay value={value} />;
}

/**
 * Format the date as a string
 * @param value
 * @param builderTimeLocale pass the builders current time locale. This is stored in react context, grab with withTimezoneDetails
 * @param showDate Shows the date (Month, Day, Year)
 * @param showTime Shows the time, will appear after date
 * @param showDayOfWeek Shows the day of week (Mon, Tue, Wed...) at the beginning of the date
 */
export function formatDate(
    value: moment.Moment,
    builderTimeLocale: TimeLocale,
    options: IDateDisplayFormattingProps,
): string {
    if (value === null || value === undefined) {
        throw `formatDate() was passed a value that was set to "${value}"`;
    }

    let valAsDate: Date;
    if (value instanceof Date) {
        valAsDate = value;
    } else {
        // convert moment.js object to Date
        valAsDate = value.toDate();
    }

    const dayOfMonth: string = "D";
    const month: string = "MMM ";
    const year: string = "YYYY";
    let time: string = "";
    let format: string = "";
    const prefix: string = "";
    let spacer: string = "";

    const dayOfWeek = "ddd";

    // 24 hour format uses 24hour:minute - 12 hour format uses 12hour:minute AM/PM
    if (builderTimeLocale.is24HourClock) {
        time = "HH:mm";
    } else {
        time = "h:mm A";
    }

    if (options.showDate) {
        if (options.showDayOfWeek) {
            format = dayOfMonth;
        } else {
            format = prefix + dayOfMonth;
        }

        if (options.showMonth) {
            format = month + format;
        }

        if (options.showYear && (!options.hideCurrentYear || !value.isSame(moment(), "year"))) {
            spacer = ", ";
            format = format + spacer + year;
        }
    }

    if (options.showTime) {
        if (format === "") {
            spacer = "";
        } else {
            spacer = ", ";
        }

        format = format + spacer + time;
    }

    if (options.showDayOfWeek) {
        if (format === "") {
            spacer = "";
        } else {
            spacer = ", ";
        }

        format = prefix + dayOfWeek + spacer + format;
    }

    return moment(valAsDate).format(format);
}
